import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { animated, useTransition } from "@react-spring/web";
import { Col } from "react-bootstrap";

import _ from "lodash";
import cx from "classnames";

import Button from "src/components/button";
import TinyMCE from "react-tinymce/lib/components/TinyMCE";
import { DEFAULT_FONTS } from "src/constants/fonts";
import contentStyle from "./content.scss";

import { editCourse, fetchDraftCourse, invalidateDraftCourse, invalidatePublishCourse, publishCourse, updateCourse, updateCourseProperty, updateValidationErrorsForCourse, validateCourse } from "src/redux/actions/coaching/courses";
import { fetchCourseLearners } from "src/redux/actions/coaching/learners";

import {
  getCoursePreferences,
  getIndexForContent,
  prepareCourseRequest,
  validateCourseRequest,
} from "./helper";

import pluralize from "pluralize";

import { EditCourseThumbnail } from "./thumbnail";
import TextareaAutosize from "react-autosize-textarea";
import { Course, CourseValidationError } from "src/types/coaching/courses";
import { Banner } from "src/types/common";
import { BannerView } from "src/components/banner";
import { AssessmentCard, LessonCard, SectionCard } from "./components/cards";
import { CourseMenuBar, SaveLearningsDialog } from "./components/menu";
import Reveal from "src/components/animation";
import { CourseLearners } from "./components/learners";
import { SelectSkillsDialog } from "./components/selectSkills";
import Thumbnail from "src/components/thumbnail";
import { AddLearnersDialog } from "./components/learners/add";
import { ScrollableContainer } from "src/components/scroller";

import { PassPercentage } from "./components/percentage";
import { CourseContext, CourseContextProps } from "./context";
import EmptyMessage from "src/components/emptyMessage";
import InfiniteScroll from "src/components/infiniteScroll";

import styles from "./edit.module.scss";
import { getCourseCreated, LoadingCoursePage } from "src/pages/coaching/components/course/view";

import userRoleService from "src/utils/userRoles.service";
import PermissionDenined from "src/components/permissionDenied";
import { CourseNotFound } from "src/pages/coaching/components/notFound";
import Text from "src/components/text";
import { FileService } from "src/utils/file.service";
import moment from "moment";

export const EditCoursePage: React.FC<object> = () => {
  const dispatch = useDispatch();

  const params = useParams();
  const id = params.id;

  const persistedCourse: Course = useSelector(
    (state: any) => _.get(state.courses, `course[${id}].item`) as Course,
  );

    const course: Course = useSelector((state: any) => {
    return _.get(state.courses, `edit.item`) as Course;
  });

  useEffect(() => {
    if (id) {
      dispatch(fetchDraftCourse(id));
    }

    return () => {
      if (id) {
      dispatch(invalidateDraftCourse(id!));
      }
    };
  }, [id]);

  useEffect(() => {
    if (persistedCourse && persistedCourse.id !== course?.id) {
      dispatch(editCourse(persistedCourse));
      dispatch(validateCourse(persistedCourse.id!));
    }
  }, [persistedCourse, course]);

  return <Outlet />
}

export const EditCourseDetailsPage: React.FC<object> = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const params = useParams();
  const id = params.id;

  const account = useSelector((state: any) => _.get(state, "accounts.item"));
  const customfontStyle = _.get("account", "theme.customStyleSheet", "");

  const nameInputRef = React.useRef<any>(null);
  const descriptionInputRef = React.useRef<any>(null);

  const persistedCourse: Course = useSelector(
    (state: any) => _.get(state.courses, `course[${id}].item`) as Course,
  );

  const loading = useSelector((state: any) => {
    return _.get(state.courses, `course[${id}].loading`);
  });

  const course: Course = useSelector((state: any) => {
    return _.get(state.courses, `edit.item`) as Course;
  });

  const courseErrorCode = useSelector((state: any) => {
    return _.get(state.courses, `course[${id}].error.errors[0].code`);
  });

  const { publishing, published } = useSelector((state: any) => {
    return {
      publishing: _.get(state.courses, 'publish.publishing'),
      published: _.get(state.courses, 'publish.published')
    }
  });

  const name = useMemo(() => {
    return _.get(course, "name") || "";
  }, [course]);

  const description = useMemo(() => {
    return _.get(course, "description") || "";
  }, [course]);

  const banner = useMemo(() => {
    return _.get(course, "banner");
  }, [course]);

  const passPercentage = useMemo(() => {  
    return _.get(course, "passPercentage") || 0;
  }, [course]);

  const courseUpdated = !_.isEqual(_.omit(course, "hasChanges", "lastPublished", "duration", "publishedBy", "status"),  
  _.omit(persistedCourse, "hasChanges", "lastPublished", "duration", "publishedBy", "status"));
  const completedLearners = useMemo(() => {
    return _.get(course, "completedLearners") || 0;
  }, [course]);

  const totalLearners = useMemo(() => {
    return _.get(course, "totalLearners") || 0;
  }, [course]);

  const passedLearners = useMemo(() => {
    return _.get(course, "passedLearners") || 0;
  }
, [course]);

  const averageScore = useMemo(() => {
    return _.get(course, "averageScore") || 0;
  }, [course]);

  const completedLearnerAttempts = useMemo(() => {
    return _.get(course, "completedLearnerAttempts") || 0;
  }, [course]);

  const totalTimeSpent = useMemo(() => {
    return _.get(course, "totalTimeSpent") || 0;
  }, [course]);

  const averageTimeSpent = useMemo(() => {
    return _.get(course, "averageTimeSpent") || 0;
  }, [course]);

  const { loadingLearners, learners, currentPage, nextPage, hasMoreItems } = useSelector((state: any) => {
    const learnersState = _.get(state, 'courses.learners');
    const loadingLearners = _.get(learnersState, 'loading');
    const pagination = _.get(learnersState, 'pagination');
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");
    const currentPage = _.get(pagination, "currentPage", 0);

    const learners = (_.get(learnersState, 'items') || []);

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      loadingLearners: loadingLearners,
      learners: learners,
      currentPage: currentPage,
      nextPage: nextPage,
      hasMoreItems: hasMoreItems
    };
  }, _.isEqual);

  const getCompletionInfo = (completed: number, total: number) => {
    const rate = total > 0 ? Math.round((completed / total) * 100) : 0;
    const text = `${completed} of ${total} learners have completed`;
  
    return { rate, text };
  };

  const getAvgScoreMetric = (averageScore: number, completedLearnerAttempts: number) => {  
    return {
      value: completedLearnerAttempts > 0 ? `${Math.round((averageScore) * 100)}%`  : "na",
      subTitle: completedLearnerAttempts > 0
        ? `across ${completedLearnerAttempts} completed ${pluralize("attempt", completedLearnerAttempts)}`
        : "No learners have taken the learning yet"
    };
  };

  const completionInfo = getCompletionInfo(completedLearners, totalLearners);
  const avgScoreMetric = getAvgScoreMetric(averageScore, completedLearnerAttempts);

  const [editThumbnail, setEditThumbnail] = useState(false);
  const [showSkillSlider, setShowSkillSlider] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [showAddLearner, setShowAddLearner] = useState(false);
  const [showSaveChangesDialog, setShowSaveChangesDialog] = useState(false);

  const [menuState, setMenuState] = useState({
    primaryAction: null,
    secondaryAction: null,
  });

  const [courseContext, setCourseContext] = useState<CourseContextProps>({
    id: id,
    courseContent: undefined,
    preferences: getCoursePreferences(),
    updatePreferences: () => { },
    gotoError: () => { },
  });

  const editTransitions = useTransition(!isPublished, {
    initial: { opacity: 1 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 300 },
  })

  const fonts = useMemo(() => {
    let fonts = [...DEFAULT_FONTS];
    const customFonts = _.get(account, "theme.fonts", []);

    if (customFonts && customFonts.length > 0) {
      fonts = [...customFonts, ...fonts];
    }

    return fonts
      .map((font) => {
        return `${font.name}=${font.fontFamily}`;
      })
      .join(";");
  }, [account]);

  const nameError = useSelector((state: any) => {
    const errors = _.get(state.courses, `edit.errors`) || [];
    return _.find(errors, { type: "course", property: "name" });
  });

  const bannerError = useSelector((state: any) => {
    const errors = _.get(state.courses, `edit.errors`) || [];
    return _.find(errors, { type: "course", property: "banner" });
  });

  const descriptionError = useSelector((state: any) => {
    const errors = _.get(state.courses, `edit.errors`) || [];
    return _.find(errors, { type: "course", property: "description" });
  });


  const userProfile = useSelector((state: any) => {
    const profile = _.get(state, "users.me");
    return {
      icon: _.get(profile, "icon"),
    }
  }, _.isEqual);

  const contents = useMemo(() => course?.contents || [], [course]);

  const createdBy = useMemo(()=> _.get(course,"publishedBy.icon",null),[course]);
  const firstName = useMemo(()=> _.get(course,"publishedBy.firstName", ""), [course] );
  const lastName = useMemo(()=> _.get(course,"publishedBy.lastName", ""), [course] );
  const created = moment(_.get(course, "createdDate"));
  const duration = useMemo(() => _.get(course,"duration",0), [course]);
  
  const handlePropertyChange = (property: string, value: any) => {
    dispatch(
      updateCourseProperty({
        course: course.id!,
        property,
        value,
      }),
    );
  };

  const handleNameChange = (e: any) => {
    let input = e.target.value;
    input = input.replace(/\n/g, "");
    handlePropertyChange("name", input);
  };

  const handleEditorSetup = useCallback((editor: any) => {
    descriptionInputRef.current = editor;
  }, [])

  const handleDescriptionChange = (e: any) => {
    const content = e.target.getContent();
    handlePropertyChange("description", content);
  };

  const handleThumbnailChange = (banner: Banner) => {
    handlePropertyChange("banner", banner);
    setEditThumbnail(false);
  };

  const handlePercentageChange = (e: number) => {
    handlePropertyChange("passPercentage", e);
  }

  const handleSave = () => {
    const validationErrors = validateCourseRequest(course);

    dispatch(updateValidationErrorsForCourse({
      course: course.id!,
      errors: validationErrors,
    }));

    if (_.size(validationErrors) > 0) {
      setShowSaveChangesDialog(false)
      courseContext.setShowValidationErrorDialog?.(true);
      return;
    }

    const request = prepareCourseRequest(persistedCourse, course);

    dispatch(
      updateCourse({
        id: course.id!,
        request: request,
      }),
    );
  };

  const { lessonCount, assessmentCount } = useMemo(() => {
      let lessonCount = 0;
      let assessmentCount = 0;
  
      const countContents = (items: any[]) => {
        items.forEach((item) => {
          if (item.entityType === "lesson") lessonCount++;
          if (item.entityType === "assessment") assessmentCount++;
          if (item.entityType === "section" && _.size(item.contents) > 0) {
            countContents(item.contents);
          }
        });
      };
  
      countContents(contents);
  
      return { lessonCount, assessmentCount };
    }, [contents]);


  const handlePublish = () => {
    const validationErrors = validateCourseRequest(course);
    dispatch(
      updateValidationErrorsForCourse({
        course: course.id!,
        errors: validationErrors,
      }),
    );

    if (_.size(validationErrors) > 0) {
      courseContext.setShowValidationErrorDialog?.(true);
      return;
    }

    dispatch(publishCourse(course.id!));
  };

  const onSkillClicked = () => {
    setShowSkillSlider(true);
  };

  const handleMenuStateChange = (state) => {
    setMenuState(state);
  };

  const handleContentClicked = (index) => {
    navigate(`/coaching/manage/courses/draft/${id}/contents?index=${index}`);
  };

  const handleSkillRemove = (skill) => {
    const items = course.skills?.filter((item: any) => item.id !== skill.id);
    handlePropertyChange("skills", items);
  };

  const handleAddSkillsFinish = (skills) => {
    handlePropertyChange("skills", skills);
  };


  const handleShowValidationErrorDialog = (show: boolean) => {
    setCourseContext((prev) => {
      return {
        ...prev,
        showValidationErrorDialog: show,
      };
    });
  };

  const handleGoToError = (error?: CourseValidationError) => {
    setCourseContext((prev) => {
      return {
        ...prev,
        validationError: error,
      };
    });
  };

  const handleMoreLearners = () => {
    if (nextPage) {
      const request = {
        course: id,
        status: "ALL",
        page: nextPage,
        limit: 10,
      };
      dispatch(fetchCourseLearners(request));
    }
  }

  const getAuthorDetails = (firstName, lastName) => {
    return`${firstName} ${lastName ?? ""}`;
  };

  const onBackClick = () => {
    if (!courseUpdated) {
      navigate("/coaching/manage");
    }

    if (courseUpdated) {
      setShowSaveChangesDialog(true);
    }
  };

  useEffect(() => {
    if (id) {
      const courseLearnerRequest = {
        course: id,
        status: "ALL",
        page: 0,
        limit: 10,
      };
      dispatch(fetchCourseLearners(courseLearnerRequest));
    }

    setCourseContext((prev) => {
      return {
        ...prev,
        id: id,
        gotoError: handleGoToError,
        setShowValidationErrorDialog: handleShowValidationErrorDialog,
      };
    });
  }, [id]);

  useEffect(() => {
    if (menuState.primaryAction === "save") {
      handleSave();
      setMenuState((prev) => {
        return {
          ...prev,
          primaryAction: null,
        };
      });
    } else if (menuState.primaryAction === "publish") {
      handlePublish();
      setMenuState((prev) => {
        return {
          ...prev,
          primaryAction: null,
        };
      });
    }
  }, [menuState]);

  useEffect(() => {
    if (courseContext.validationError) {
      const type = courseContext.validationError.type;
      const uuid = courseContext.validationError.uuid;
      const property = courseContext.validationError.property;

      switch (type) {
        case "course": {
          navigate(`/coaching/manage/courses/draft/${id}`);
          if (property === "name") {
            setTimeout(() => {
              nameInputRef.current?.focus();
            }, 1000);
          } else if (property === "description") {
            setTimeout(() => {
              descriptionInputRef.current?.focus();
            }, 1000);
          } else if (property === "banner") {
            setEditThumbnail(true);
          } else if (property === "contents") {
            navigate(`/coaching/manage/courses/draft/${id}/contents`);
          } else if (property === "skills") {
            setShowSkillSlider(true);
          }
          courseContext.gotoError(undefined);
          break;
        }

        case 'assessment': {
          const index = _.findIndex(course.contents, (content) => content.uuid === uuid);
          navigate(`/coaching/manage/courses/draft/${id}/contents?index=${index}`);
          break;
        }

        case 'lesson': {
          const index = _.findIndex(course.contents, (content) => content.uuid === uuid);
          navigate(`/coaching/manage/courses/draft/${id}/contents?index=${index}`);
          courseContext.gotoError(undefined);
          break;
        }

        default: {
          break;
        }
      }
    }
  }, [courseContext.validationError]);

  useEffect(() => {
    if (published) {
      setIsPublished(true);
    }
  }, [published]);
  
  useEffect(() => {
    const editor = descriptionInputRef.current;
    if (!editor) return;

    if (description !== editor.getContent()) {
      editor.setContent(description || "");
    }
  }, [description]);

  if (!userRoleService.canCreateCourse()) {
    return <PermissionDenined className={styles.permissionDenied} />;
  }

  if (courseErrorCode === 10007) {
    return <CourseNotFound onBack={() => {
      navigate(`/coaching/manage`);
    }} />
  }

  if ((loading && course?.id != id) || !course) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.backContainer}>
            <div
              className={styles.back}
              onClick={() => {
                navigate(`/coaching/manage`);
              }}
            >
              <i className="paperflite-left-arrow"></i>
              <span>courses</span>
            </div>
          </div>
        </div>
        <div className={styles.editContainer}>
          <div className={styles.left}>
            <LoadingCoursePage />
          </div>
          <div className={styles.right}></div>
        </div>
      </div>
    );
  }

  return (
    <CourseContext.Provider value={courseContext}>
      <div key={course.id} className={styles.container}>
        {!isPublished && (
          <div className={styles.header}>
            <div className={styles.backContainer}>
              <div
                className={styles.backButton}
                onClick={onBackClick}
              >
                <i className="paperflite-left-arrow"></i>
                <div>
                  <span className={styles.back}>back to</span>{" "}
                  <span>"manage courses"</span>
                </div>
              </div>
            </div>
            <div className={styles.menubar}>
              <CourseMenuBar
                course={course}
                menuState={menuState}
                onMenuStateChange={handleMenuStateChange}
              />
            </div>
          </div>
        )}

        {editTransitions((transitionStyles, item) =>
          item && (
            <animated.div
              style={transitionStyles}>
              <div className={styles.editContainer}>
              <div className={styles.left}>
                <div className={styles.details}>
                  <div className={cx(styles.field, styles.name)}>
                    <TextareaAutosize
                      className={styles.input}
                      ref={nameInputRef}
                      placeholder="Enter course name..."
                      rows={1}
                      maxRows={10}
                      value={name}
                      data-testid="course-name"
                      onChange={handleNameChange}
                      onPointerEnterCapture={() => { }}
                      onPointerLeaveCapture={() => { }}
                    />
                    <Reveal show={nameError != null}>
                      <div className={styles.error}>
                        <i className="fa-solid fa-triangle-exclamation"></i>
                        <span>{nameError?.message}</span>
                      </div>
                    </Reveal>
                  </div>

                  <div
                    className={cx(styles.field, styles.thumbnail)}
                    onClick={() => {
                      setEditThumbnail(true);
                    }}
                  >
                    <div className={styles.inner}>
                      <div className={styles.overlay}>
                        <i className="paperflite-edit"></i>
                      </div>
                      <BannerView
                        banner={banner as any}
                        defaultBanner="/images/assets/courseThumbnail.jpg"
                        overlay={false}
                      />
                    </div>
                    <Reveal show={bannerError != null}>
                      <div className={styles.error}>
                        <i className="fa-solid fa-triangle-exclamation"></i>
                        <span>{bannerError?.message}</span>
                      </div>
                    </Reveal>
                  </div>
                  <div className={styles.courseMetadata}>
                    <div className={styles.metadata}>
                      <i className="paperflite-file" />
                      {lessonCount}{" "}
                      {pluralize('lesson', lessonCount)}
                    </div>
                    <div className={styles.metadata}>
                      <i className="paperflite-document" />
                      {assessmentCount}{" "}
                      {pluralize('assessment', assessmentCount)}
                    </div>
                    {duration > 0 && 
                    <div className={styles.metadata}>
                      <i className="paperflite-clock" />
                      {FileService.formatDuration( duration, "long")}
                    </div>}
                    {firstName &&
                    <div className={cx(styles.metadata, styles.profile)}>
                      <Thumbnail
                        media={userProfile.icon}
                        defaultImage="/images/assets/default_asset.jpg"
                        className={styles.icon}
                      />
                      <span className={styles.details}>
                        by {getAuthorDetails(firstName, lastName)}, {" "}{getCourseCreated(created)}
                      </span>
                  </div>
                  }
                </div>
                  <div className={cx(styles.field, styles.description)}>
                    <div className={styles.label}>Description</div>
                    <TinyMCE
                      className={styles.tinyEditor}
                      content={description}
                      id={`description-${id}`}
                      data-testid="course-description"
                      config={{
                        placeholder: "Enter your course description here...",
                        inline: true,
                        branding: false,
                        statusbar: false,
                        contextmenu: false,
                        theme: "silver",
                        plugins:
                          "code, autoresize, noneditable, variable, paste, link, autolink, lists",
                        menubar: false,
                        toolbar:
                          "undo redo | bold italic | alignleft aligncenter alignright | fontselect | fontsizeselect | forecolor backcolor | code | link | cta | variables",
                        fontsize_formats:
                          "5px 5.5px 6.5px 7.5px 8px 9px 10px 10.5px 11px 12px 14px 16px 18px 20px 22px 24px 26px 28px 36px 48px 72px",
                        autoresize_bottom_margin: 0,
                        autoresize_min_height: 100,
                        paste_retain_style_properties: "color",
                        resize: false,
                        min_height: 100,
                        content_css: [
                          "https://fonts.googleapis.com/css?family=Montserrat:100,100i,200,200i,300,300i,400,400i,500,500i,600,600i,700,700i,800,800i,900,900i",
                          contentStyle,
                          customfontStyle,
                        ],
                        font_formats: fonts,
                        default_link_target: "_blank",
                        browser_spellcheck: true,
                        convert_urls: false,
                        relative_urls: false,
                        remove_script_host: false,
                        setup: handleEditorSetup,
                      }}
                      onChange={handleDescriptionChange}
                    />
                    <Reveal show={descriptionError != null}>
                      <div className={styles.error}>
                        <i className="fa-solid fa-triangle-exclamation"></i>
                        <span>{descriptionError?.message}</span>
                      </div>
                    </Reveal>
                  </div>

                  <div className={cx(styles.field, styles.contents)}>
                    {_.size(_.get(course, "contents")) > 0 && (
                      <div className={styles.label}>
                        Course Content
                      </div>
                    )}

                    {_.size(_.get(course, "contents")) == 0 && (
                      <>
                        <div className={styles.label}>Course Structure</div>
                        <EmptyCourseContent course={course.id!} />
                      </>
                    )}

                    <div className={styles.contentsList}>
                      {course.contents?.map((content, index) => {
                        const type = _.get(content, "entityType");
                        return (
                          <React.Fragment key={content.uuid}>
                            {type === "lesson" &&
                              <LessonCard
                                key={index}
                                lessonIndex={getIndexForContent(course.contents || [], content.uuid, content.parent)}
                                lesson={content}
                                index={index}
                                active={false}
                                mode='view'
                                readonly
                                onClick={handleContentClicked}
                              />
                            }
                            {type === "assessment" &&
                              <AssessmentCard
                                key={index}
                                assessment={content}
                                assessmentIndex={getIndexForContent(course.contents || [], content.uuid, content.parent)}
                                index={index}
                                active={false}
                                mode='view'
                                readonly
                                onClick={handleContentClicked}
                              />
                            }

                            {type === "section" &&
                              <SectionCard
                                key={index}
                                section={content}
                                index={index}
                                sectionIndex={getIndexForContent(course.contents || [], content.uuid, content.parent)}
                                active={false}
                                mode='view'
                                readonly
                                onClick={handleContentClicked}
                              />
                            }

                            <div className={styles.dividerLine}></div>
                          </React.Fragment>
                        );
                      })}
                    </div>
                    
                  </div>

                </div>

              </div>
              <div className={styles.right}>
                {(published || course.publishedBy) && (
                  <ScrollableContainer
                    distance={300}
                    leftArrowComponent={LeftArrow}
                    rightArrowComponent={RightArrow}
                  >
                    <div className={styles.infoContainer}>
                      <Metrics
                        name="completion rate"
                        value={completionInfo.rate + "%"}
                        subTitle={completionInfo.text}
                      />

                      <Metrics
                        name="pass rate"
                        value={`${Math.round(course?.passRate ?? 0)}%`}
                        subTitle={`${passedLearners} of ${completedLearners} ${pluralize("completed learners", completedLearners)} have passed`}
                      />

                      <Metrics
                        name="avg score"
                        value={avgScoreMetric.value}
                        subTitle={avgScoreMetric.subTitle}
                      />

                      <Metrics 
                        name="total time spent"
                        value={FileService.formatDuration(totalTimeSpent, "short")}
                        subTitle={`across ${completedLearnerAttempts} completed ${pluralize("attempt", completedLearnerAttempts)}`}
                        />

                      <Metrics 
                      name="avg time spent"
                      value={FileService.formatDuration(averageTimeSpent, "short")}
                      subTitle={`across ${completedLearnerAttempts} completed ${pluralize("attempt", completedLearnerAttempts)}`}
                      />

                    </div>
                  </ScrollableContainer>
                )}
                
                <div className={styles.skillsContainer}>
                  <p className={styles.heading}>
                    Skills, learner will get ({course.skills?.length || 0})
                  </p>
                  <div className={styles.skills}>
                    <div
                      className={styles.addSkillButton}
                      onClick={() => onSkillClicked()}
                    >
                      <i className={cx(styles.icon, "paperflite-add")}></i>
                    </div>

                    {(course.skills || []).map((skill: any) => {
                      return (
                        <div key={skill.id} className={styles.skill}>
                          <div className={styles.name}>{skill.name}</div>
                          <i
                            className={cx("paperflite-close", styles.closeIcon)}
                            onClick={() => handleSkillRemove(skill)}
                          ></i>
                        </div>
                      );
                    })}
                  </div>
                </div>
                {course.id && (
                  <div className={styles.passPercentageContainer}>
                    <PassPercentage value={passPercentage} handlePercentageChange={handlePercentageChange} />
                  </div>
                )}
                <div className={styles.courseLearnersContainer}>
                  <CourseLearners id={course.id!} course={course}/>
                </div>


              </div>
              </div>
              <div className={styles.edit}>
                {_.size(_.get(course, "contents")) > 0 && (
                  <Button
                    variant="courses-primary"
                    onClick={() =>
                      navigate(`/coaching/manage/courses/draft/${id}/contents`)
                    }
                  >
                    <div className={styles.button} >
                      <span>edit content structure</span>
                      <i className="paperflite-edit" />
                    </div>
                  </Button>
                )}
              </div>
            </animated.div>))}

        {editThumbnail && (
          <EditCourseThumbnail
            show={editThumbnail}
            type='course'
            banner={course?.banner || null}
            defaultBanner="/images/assets/courseThumbnail.jpg"
            onFinish={handleThumbnailChange}
            onClose={() => setEditThumbnail(false)}
          />
        )}

        {showSkillSlider && (
          <SelectSkillsDialog
            show={showSkillSlider}
            selected={course.skills || []}
            onFinish={handleAddSkillsFinish}
            onClose={() => setShowSkillSlider(false)}
          />
        )}
        {showSaveChangesDialog && (
        <SaveLearningsDialog
          show={showSaveChangesDialog}
          course={course}
          onFinish={() => setShowSaveChangesDialog(false)}
          onClose={() => setShowSaveChangesDialog(false)}
          handleSave={handleSave}
        />
      )}

        <PublishView
          isOpen={isPublished}
          onClose={() => setIsPublished(false)}
          learners={learners || []}
          handleAssignLearners={() => setShowAddLearner(true)}
          loading={loadingLearners}
          hasMoreItems={hasMoreItems}
          handleLoadMoreLearners={handleMoreLearners}
        />

      </div>

      <AddLearnersDialog
        show={showAddLearner}
        course={course}
        onFinish={() => setShowAddLearner(false)}
        onClose={() => setShowAddLearner(false)}
      />
    </CourseContext.Provider>
  );
};

interface EmptyCourseContentProps {
  course: string;
}

const EmptyCourseContent: React.FC<EmptyCourseContentProps> = ({ course }) => {
  const navigate = useNavigate();

  return (
    <div className={styles.emptyCourseContent}>
      <div className={styles.placeholder}>
        <div className={styles.icon}></div>
        <p className={styles.text}>
          Lessons and assessments you create will appear here
        </p>
      </div>
      <div className={styles.placeholder}>
        <Button
          variant="primary"
          icon="paperflite-right-arrow"
          className={styles.createButton}
          onClick={() =>
            navigate(`/coaching/manage/courses/draft/${course}/contents`)
          }
        >
          Create course structure
        </Button>
      </div>
    </div>
  );
};

interface PublishViewProps {
  isOpen: boolean;
  onClose: () => void;
  learners: any[];
  handleAssignLearners: () => void;
  loading: boolean;
  hasMoreItems: boolean;
  handleLoadMoreLearners: () => void;
}

export const PublishView: React.FC<PublishViewProps> = ({
  isOpen,
  onClose,
  learners,
  handleAssignLearners,
  loading,
  hasMoreItems,
  handleLoadMoreLearners,
}) => {
  const PublishCourseIcon = "/images/coaching/publish.svg";
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [closePublish, setClosePublish] = useState(false);

  const publishTransitions = useTransition(isOpen && !closePublish, {
    initial: { transform: "translateY(0%)" },
    from: { transform: "translateY(100%)" },
    enter: { transform: "translateY(0%)" },
    leave: { transform: "translateY(100%)" },
    config: { duration: 300 },
    delay: 100,
    onRest: () => {
      if (closePublish) {
        setClosePublish(false);
        dispatch(invalidatePublishCourse());
        onClose();
      }
    },
  });

  const learnersTransitions = useTransition(isOpen && !closePublish, {
    initial: { transform: "translateY(0%)" },
    from: { transform: "translateY(100%)" },
    enter: { transform: "translateY(0%)" },
    leave: { transform: "translateY(100%)" },
    config: { duration: 300 },
    delay: 1000,
  });

  useEffect(() => {
    return () => {
      dispatch(invalidatePublishCourse());
    };
  }, []);


  return publishTransitions(
    (transitionStyles, item) =>
      item && (
        <animated.div
          className={styles.publishContainer}
          style={transitionStyles}
        >
          <EmptyMessage
            width={500}
            image={PublishCourseIcon}
            heading="Congratulations!"
            message="Your course has been published."
          />
          <div className={styles.actions}>
            <Button
              variant="primary"
              icon="paperflite-right-arrow"
              text="View Course Details"
              onClick={() => setClosePublish(true)}
            />
            <Button
              variant="secondary"
              icon="paperflite-close"
              text="Exit"
              onClick={() => navigate(`/coaching/manage`)}
            />
          </div>

          {learners.length > 0 &&
            learnersTransitions(
              (transitionStyles, item) =>
                item && (
                  <animated.div
                    className={styles.learnersContainer}
                    style={transitionStyles}
                  >
                    <div className={styles.header}>
                      <div className={styles.title}>
                        Here's the list of learners whom you have assigned this
                        course to,
                      </div>
                      <Button
                        variant="tertiary"
                        icon="paperflite-user-settings"
                        text="Assign learners"
                        className={styles.assign}
                        onClick={handleAssignLearners}
                      />
                    </div>

                    <InfiniteScroll
                      className={styles.learners}
                      loading={loading}
                      hasMore={hasMoreItems}
                      loadMore={handleLoadMoreLearners}
                      loader={() => <LearnerPlaceholder />}
                    >
                      {learners.map((learner, index) => {
                        const firstName = learner?.user?.firstName || "";
                        const lastName = learner?.user?.lastName || "";
                        const fullName = `${firstName} ${lastName}`.trim() || "";

                        return (
                          <Col md={4} key={index} className={styles.learner}>
                            <Thumbnail
                              media={learner?.user?.icon}
                              defaultImage="/images/assets/default_asset.jpg"
                              className={styles.icon}
                            />

                            <Text className={styles.name} text={fullName} lines={1} />
                          </Col>
                        );
                      })}
                    </InfiniteScroll>
                  </animated.div>
                ),
            )}
        </animated.div>
      ),
  );
};


export const LearnerPlaceholder: React.FC<{}> = ({ }) => {
  return (
    <div className={styles.learnerPlaceholder}>
      <div className={styles.icon}></div>
      <div className={cx(styles.placeholder)}>
        <div className={cx(styles.name, styles.block)}></div>
      </div>
    </div>
  );
}

const LeftArrow: React.FC<any> = () => {
  return (
    <div className={cx(styles.arrow, styles.leftArrow)}>
      <i className="paperflite-arrow-up"></i>
    </div>
  );
}

const RightArrow: React.FC<any> = () => {
  return (
    <div className={cx(styles.arrow, styles.rightArrow)}>
      <i className="paperflite-arrow-up"></i>
    </div>
  );
}

interface MetricsProps {
  name: string;
  value?: any;
  subTitle?: string;
}

const Metrics: React.FC<MetricsProps> = ({name, value, subTitle}) => {
  return (
    <div className={styles.infoCard}>
      <div className={styles.metrics}>
        <span className={styles.name}>{name}</span>
        <span className={styles.value}>{value}</span>
        <span className={styles.subTitle}>{subTitle}</span>
      </div>
    </div>
  );
};